import { FunctionComponent } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation } from '@apollo/client'
import { STAFF_DELINKS } from '@/modules/API/daycare/staffLinkHandler.ts'
import styles from './unlinkStaffDialog.module.scss'
import Dialog from '@/components/_custom/Dialog/Dialog.tsx'
import DialogHeader from '@/components/_custom/DialogHeader/DialogHeader.tsx'
import DialogBody from '@/components/_custom/DialogBody/DialogBody.tsx'
import ButtonTextIcon from '@/components/_custom/Buttons/ButtonTextIcon/ButtonTextIcon.tsx'
import { Close, Delete } from '@mui/icons-material'
import { NavigateFunction, useNavigate } from 'react-router-dom'
import { useNotification } from '@/modules/context/NotificationContext.tsx'

interface UnlinkStaffDialogProps {
    open: boolean
    handleClose: () => void
    staffEmail: string
    daycareId: number
    refetchAllStaffList: () => Promise<void>
}

const UnlinkStaffDialog: FunctionComponent<UnlinkStaffDialogProps> = ({
    open,
    handleClose,
    staffEmail,
    daycareId,
    refetchAllStaffList
}: UnlinkStaffDialogProps) => {
    const { t } = useTranslation()

    const navigate: NavigateFunction = useNavigate()

    const { showNotificationSuccess, showNotificationError } = useNotification()

    const [delinkStaff] = useMutation(STAFF_DELINKS, {
        variables: {
            email: staffEmail,
            daycare_id: daycareId
        },
        onCompleted: () => {
            showNotificationSuccess(t('staffUnlinked'))

            refetchAllStaffList().then(() => {
                handleClose()
                navigate('/staff?daycare=' + daycareId, { replace: true })
            })
        },
        onError: () => {
            showNotificationError(t('errorUnlinkingStaff'))
        }
    })

    return (
        <Dialog open={open} handleClose={handleClose} className={styles.Dialog}>
            <DialogHeader title={t('confirmUnlinkStaff')} />
            <DialogBody className={styles.DialogBody}>
                <p className={styles.Text}>{t('confirmUnlinkStaffMessage')}</p>
                <p className={styles.Email}>{staffEmail}</p>
                <p className={styles.Text}>{t('noRollBack')}</p>
                <div className={styles.ButtonContainer}>
                    <ButtonTextIcon
                        type={'custom'}
                        customText={t('yes')}
                        customIcon={<Delete />}
                        onClick={delinkStaff}
                        className={styles.Button}
                    />
                    <ButtonTextIcon
                        type={'custom'}
                        customText={t('no')}
                        customIcon={<Close />}
                        onClick={handleClose}
                        className={styles.Button}
                    />
                </div>
            </DialogBody>
        </Dialog>
    )
}

export default UnlinkStaffDialog
